import type { Qty } from "./quantity";

export const UNIT_CODES = ["pcs", "pair", "set", "l", "ml", "m", "cm", "kg", "g"] as const;
export type UnitCode = (typeof UNIT_CODES)[number];

/**
 * `decimals`: fraction digits users may enter. `step`: the −/+ stepper increment in milli-units.
 */
export const UNITS: Record<UnitCode, { decimals: number; step: Qty }> = {
  pcs: { decimals: 0, step: 1000 },
  pair: { decimals: 0, step: 1000 },
  set: { decimals: 0, step: 1000 },
  l: { decimals: 2, step: 1000 },
  ml: { decimals: 0, step: 100_000 },
  m: { decimals: 2, step: 1000 },
  cm: { decimals: 0, step: 10_000 },
  kg: { decimals: 3, step: 1000 },
  g: { decimals: 0, step: 100_000 },
};

export function isUnitCode(value: string): value is UnitCode {
  return (UNIT_CODES as readonly string[]).includes(value);
}

/**
 * Rounds an amount up to what the unit allows: 6.5 of a part counted in pieces is 7, 6.555 L is
 * 6.56 L. Never down, so what a job needs or an order asks for is never short. A removal (negative
 * amount) rounds away from zero the same way: taking out 2.5 pieces takes out 3.
 */
export function roundUpToUnit(qty: Qty, unit: UnitCode): Qty {
  const granularity = 10 ** (3 - UNITS[unit].decimals);
  const rounded = Math.ceil(Math.abs(qty) / granularity) * granularity;
  return qty < 0 ? -rounded : rounded;
}

export function unitStep(unit: UnitCode): Qty {
  return UNITS[unit].step;
}
